import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ExerciseLibraryPanel = ({ exercises, onAddExercise, selectedExerciseIds }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('all');

  const categories = ['all', 'strength', 'cardio', 'flexibility', 'balance', 'sports'];
  const muscleGroups = ['all', 'chest', 'back', 'legs', 'shoulders', 'arms', 'core', 'full-body'];

  const filteredExercises = exercises?.filter(ex => {
    const matchesSearch = ex?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || ex?.category === selectedCategory;
    const matchesMuscle = selectedMuscleGroup === 'all' || ex?.muscleGroups?.includes(selectedMuscleGroup);
    return matchesSearch && matchesCategory && matchesMuscle;
  });

  const categoryIcons = {
    strength: 'Dumbbell',
    cardio: 'Heart',
    flexibility: 'Sparkles',
    balance: 'Target',
    sports: 'Trophy'
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 h-[calc(100vh-250px)] flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Library" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Exercise Library</h2>
      </div>
      <div className="space-y-3 mb-4">
        <Input
          placeholder="Search exercises..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="w-full"
        />

        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e?.target?.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
          >
            {categories?.map(cat => (
              <option key={cat} value={cat}>
                {cat?.charAt(0)?.toUpperCase() + cat?.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Muscle Group</label>
          <select
            value={selectedMuscleGroup}
            onChange={(e) => setSelectedMuscleGroup(e?.target?.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
          >
            {muscleGroups?.map(muscle => (
              <option key={muscle} value={muscle}>
                {muscle?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="text-xs text-muted-foreground mb-2">
        {filteredExercises?.length} exercises
      </div>
      <div className="flex-1 overflow-y-auto space-y-2">
        {filteredExercises?.map((exercise) => {
          const isAdded = selectedExerciseIds?.includes(exercise?.id);
          return (
            <motion.div
              key={exercise?.id}
              className={`p-3 rounded-lg border transition-all ${
                isAdded
                  ? 'bg-primary/10 border-primary/30' :'bg-muted border-border hover:border-primary/50'
              }`}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon
                    name={categoryIcons?.[exercise?.category] || 'Dumbbell'}
                    size={16}
                    className="text-primary"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground truncate">
                    {exercise?.name}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                    {exercise?.description}
                  </p>
                  {exercise?.muscleGroups?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {exercise?.muscleGroups?.slice(0, 2)?.map((muscle, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground"
                        >
                          {muscle}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={isAdded ? 'outline' : 'default'}
                  onClick={() => onAddExercise(exercise)}
                  disabled={isAdded}
                  className="flex-shrink-0"
                >
                  <Icon name={isAdded ? 'Check' : 'Plus'} size={14} />
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ExerciseLibraryPanel;